/* ==========================================================================
   Aspire Leadership — live hero background
   A continuously running gradient mesh: slow-morphing brand-coloured lobes,
   drifting light points, sine domain-warp, cursor-reactive parallax.

   Raw WebGL, no library. Colours are read from the CSS custom properties so
   the shader stays token-driven and the client's theme-editor changes reach
   it. Falls back to the CSS gradient already painted behind the canvas if
   WebGL is unavailable or the visitor prefers reduced motion.
   ========================================================================== */
(function () {
  'use strict';

  var canvas = document.querySelector('[data-hero-canvas]');
  if (!canvas) return;
  canvas.style.imageRendering = 'auto';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) return;                       /* static CSS gradient stays */

  var gl = canvas.getContext('webgl', { alpha: true, antialias: false, powerPreference: 'high-performance' })
        || canvas.getContext('experimental-webgl');
  if (!gl) return;                           /* static CSS gradient stays */

  /* ------------------------------------------------ read brand tokens */
  function tokenRGB(name, fallback) {
    var raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    var m = raw.match(/^#?([0-9a-f]{6})$/i);
    if (m) {
      var n = parseInt(m[1], 16);
      return [(n >> 16 & 255) / 255, (n >> 8 & 255) / 255, (n & 255) / 255];
    }
    var p = raw.split(',').map(Number);
    if (p.length === 3 && p.every(function (v) { return !isNaN(v); })) {
      return [p[0] / 255, p[1] / 255, p[2] / 255];
    }
    return fallback;
  }

  var cDeep = tokenRGB('--brand-deep', [0.161, 0.063, 0.078]);
  var cWine = tokenRGB('--brand-secondary', [0.569, 0.227, 0.290]);
  var cRose = tokenRGB('--brand-primary', [0.788, 0.482, 0.537]);
  var cAcc  = tokenRGB('--brand-accent', [0.784, 0.208, 0.294]);

  /* ------------------------------------------------------------ shaders */
  var VERT = [
    'attribute vec2 p;',
    'void main(){ gl_Position = vec4(p, 0.0, 1.0); }'
  ].join('\n');

  var FRAG = [
    'precision highp float;',
    'uniform vec2  uRes;',
    'uniform float uTime;',
    'uniform vec2  uPointer;',
    'uniform float uQuality;',   /* 1.0 full density, scales light-point count */
    'uniform vec3  uDeep;',
    'uniform vec3  uWine;',
    'uniform vec3  uRose;',
    'uniform vec3  uAcc;',

    /* --- a soft moving lobe of colour -------------------------------- */
    'float lobe(vec2 uv, vec2 c, float r){',
    '  float d = length(uv - c);',
    '  return smoothstep(r, 0.0, d);',
    '}',

    /* --- aurora ribbon: a flowing band of light across the field ----- */
    'float ribbon(vec2 uv, float phase, float speed, float thick){',
    '  float y = sin(uv.x * 2.1 + uTime * speed + phase) * 0.30',
    '          + sin(uv.x * 3.7 - uTime * speed * 0.62 + phase * 1.7) * 0.17',
    '          + sin(uv.x * 1.3 + uTime * speed * 0.41 + phase * 2.6) * 0.22;',
    '  float d = abs(uv.y - y);',
    '  return smoothstep(thick, 0.0, d);',
    '}',

    'void main(){',
    '  vec2 uv = (gl_FragCoord.xy - 0.5 * uRes) / min(uRes.x, uRes.y);',
    '  float t = uTime * 0.13;',           /* ~2x the old pace */

    /* pointer parallax — the field leans toward the cursor */
    '  uv += uPointer * 0.10;',

    /* Domain warp — the field churns rather than sliding. Built from sines
       rather than fbm: on a soft gradient the difference is invisible, and
       value noise was by far the most expensive thing in this shader. */
    '  vec2 warp = vec2(',
    '    sin(uv.y * 3.1 + t * 1.7) * 0.5 + sin(uv.x * 2.3 - t * 1.1) * 0.3,',
    '    cos(uv.x * 2.7 - t * 1.3) * 0.5 + cos(uv.y * 3.6 + t * 0.9) * 0.3',
    '  );',
    '  vec2 quv = uv + warp * 0.30;',

    /* four drifting lobes on independent orbits */
    '  vec2 c1 = vec2(sin(t * 1.10) * 0.50, cos(t * 0.86) * 0.36);',
    '  vec2 c2 = vec2(cos(t * 0.74) * 0.60, sin(t * 1.32) * 0.44) + vec2(0.18, -0.12);',
    '  vec2 c3 = vec2(sin(t * 0.52 + 2.1) * 0.44, cos(t * 0.63 + 1.4) * 0.52) - vec2(0.22, 0.08);',
    '  vec2 c4 = vec2(cos(t * 1.42 + 0.7) * 0.38, sin(t * 0.98 + 3.1) * 0.46);',

    '  vec3 col = uDeep;',
    '  col = mix(col, uWine, lobe(quv, c1, 0.98) * 1.00);',
    '  col = mix(col, uRose, lobe(quv, c2, 0.82) * 0.85);',
    '  col = mix(col, uAcc,  lobe(quv, c3, 0.70) * 0.72);',
    '  col = mix(col, uRose, lobe(quv, c4, 0.55) * 0.55);',

    /* --- three aurora ribbons, the main "alive" read -------------- */
    '  float r1 = ribbon(quv, 0.0, 0.42, 0.20);',
    '  float r2 = ribbon(quv * 1.15, 2.4, 0.31, 0.14);',
    '  float r3 = ribbon(quv * 0.85, 4.9, 0.55, 0.10);',
    '  col += uRose * r1 * 0.55;',
    '  col += uAcc  * r2 * 0.48;',
    '  col += vec3(1.0) * r3 * 0.12;',

    /* --- energy rings pulsing out from the centre ----------------- */
    '  float rad = length(uv);',
    '  for (int k = 0; k < 3; k++){',
    '    float ph = fract(uTime * 0.16 + float(k) * 0.3333);',
    '    float rr = ph * 1.5;',
    '    float ring = smoothstep(0.035, 0.0, abs(rad - rr));',
    '    col += uAcc * ring * (1.0 - ph) * 0.55;',
    '  }',

    /* --- drifting light points with a real bloom ------------------ */
    '  float pts = 0.0;',
    '  float halo = 0.0;',
    '  const int MAXP = 11;',
    '  for (int i = 0; i < MAXP; i++){',
    '    float fi = float(i);',
    '    if (fi >= uQuality * float(MAXP)) break;',
    '    float sp = 0.30 + fract(fi * 0.379) * 0.75;',
    '    vec2 pc = vec2(',
    '      sin(uTime * sp * 0.26 + fi * 2.399) * 0.78,',
    '      cos(uTime * sp * 0.19 + fi * 1.117) * 0.58',
    '    );',
    '    float d = length(uv - pc);',
    '    float tw = 0.45 + 0.55 * sin(uTime * 1.6 + fi * 3.3);',
    '    pts  += smoothstep(0.034, 0.0, d) * tw;',
    '    halo += smoothstep(0.200, 0.0, d) * tw * 0.42;',
    '  }',
    '  col += vec3(1.0) * pts * 0.55;',
    '  col += uRose * halo * 1.15;',
    '  col += uAcc  * halo * 0.55;',

    /* fine grain so the gradient never bands on wide screens */
    '  float g = fract(sin(dot(gl_FragCoord.xy + uTime, vec2(12.9898, 78.233))) * 43758.5453);',
    '  col += (g - 0.5) * 0.030;',

    /* Gentle vignette. Deliberately light — the scrim above the canvas
       carries headline contrast, so the field itself can stay vivid. */
    '  float vig = smoothstep(1.55, 0.20, rad);',
    '  col = mix(uDeep * 0.96, col, vig * 0.72 + 0.28);',

    /* lift saturation so the brand colours actually read as colour */
    '  float lum = dot(col, vec3(0.2126, 0.7152, 0.0722));',
    '  col = mix(vec3(lum), col, 1.28);',

    '  gl_FragColor = vec4(col, 1.0);',
    '}'
  ].join('\n');

  function compile(type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.warn('[aspire hero]', gl.getShaderInfoLog(s));
      return null;
    }
    return s;
  }

  var vs = compile(gl.VERTEX_SHADER, VERT);
  var fs = compile(gl.FRAGMENT_SHADER, FRAG);
  if (!vs || !fs) return;

  var prog = gl.createProgram();
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
  gl.useProgram(prog);

  var buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  var loc = gl.getAttribLocation(prog, 'p');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

  var U = {
    res: gl.getUniformLocation(prog, 'uRes'),
    time: gl.getUniformLocation(prog, 'uTime'),
    pointer: gl.getUniformLocation(prog, 'uPointer'),
    quality: gl.getUniformLocation(prog, 'uQuality'),
    deep: gl.getUniformLocation(prog, 'uDeep'),
    wine: gl.getUniformLocation(prog, 'uWine'),
    rose: gl.getUniformLocation(prog, 'uRose'),
    acc: gl.getUniformLocation(prog, 'uAcc')
  };
  gl.uniform3fv(U.deep, cDeep);
  gl.uniform3fv(U.wine, cWine);
  gl.uniform3fv(U.rose, cRose);
  gl.uniform3fv(U.acc, cAcc);

  /* ------------------------------------------------------------- sizing */
  var RESOLUTION_SCALE = 0.55;
  var dpr = 1;
  function resize() {
    /* Cap DPR: the effect is a soft gradient, so rendering at 3× buys
       nothing visible and costs a lot of fill rate on phones. */
    /* RESOLUTION_SCALE is the single biggest lever on this effect: the field
       is a soft gradient with no fine detail, so rendering at ~0.55x and
       letting the browser upscale is visually identical and costs about a
       third of the fragments. */
    dpr = Math.min(window.devicePixelRatio || 1, 1.5) * RESOLUTION_SCALE;
    var w = Math.floor(canvas.clientWidth * dpr);
    var h = Math.floor(canvas.clientHeight * dpr);
    if (canvas.width === w && canvas.height === h) return;
    canvas.width = w;
    canvas.height = h;
    gl.viewport(0, 0, w, h);
    gl.uniform2f(U.res, w, h);
  }
  window.addEventListener('resize', resize, { passive: true });
  resize();

  /* ------------------------------------------------------------ pointer */
  var ptx = 0, pty = 0, tgx = 0, tgy = 0;
  function onPoint(x, y) {
    tgx = (x / window.innerWidth) * 2 - 1;
    tgy = -((y / window.innerHeight) * 2 - 1);
  }
  window.addEventListener('pointermove', function (e) { onPoint(e.clientX, e.clientY); }, { passive: true });
  window.addEventListener('deviceorientation', function (e) {
    if (e.gamma == null) return;
    tgx = Math.max(-1, Math.min(1, e.gamma / 35));
    tgy = Math.max(-1, Math.min(1, (e.beta - 45) / 35));
  }, { passive: true });

  /* --------------------------------------------------- adaptive density */
  /* Client decision: the effect stays on mobile at full power. If a device
     genuinely can't hold ~30fps we thin the light points — the density
     adjusts, the effect does not get switched off. */
  var quality = 1.0;
  var frames = 0, fpsStart = performance.now(), settled = false;

  /* Pause entirely when the hero is off-screen or the tab is hidden. */
  var visible = true;
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(function (entries) {
      visible = entries[0].isIntersecting;
    }, { threshold: 0.01 }).observe(canvas);
  }

  var start = performance.now();
  function frame(now) {
    requestAnimationFrame(frame);
    if (!visible || document.hidden) return;

    resize();
    ptx += (tgx - ptx) * 0.045;
    pty += (tgy - pty) * 0.045;

    gl.uniform1f(U.time, (now - start) / 1000);
    gl.uniform2f(U.pointer, ptx, pty);
    gl.uniform1f(U.quality, quality);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    if (settled) return;
    frames++;
    if (now - fpsStart >= 2000) {
      var fps = frames / ((now - fpsStart) / 1000);
      if (fps < 30 && quality > 0.35) {
        quality = Math.max(0.35, quality - 0.3);
        frames = 0; fpsStart = now;         /* re-measure at the new density */
      } else {
        settled = true;                      /* stop measuring, stop churning */
      }
    }
  }
  requestAnimationFrame(frame);
})();
